import { Box, Card, CardContent, Typography, Divider, Stack } from "@mui/material";
import {
  People,
  ShoppingCart,
  BarChart,
  Timeline,
  Payment as PaymentIcon,
  Schedule,
  Message,
} from "@mui/icons-material";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";

const DashboardHome = () => {
  // ---- stats ----
  const [stats, setStats] = useState({});

  // ---- distributions (pie charts) ----
  const [monthlyBotData, setMonthlyBotData] = useState([]);                // bu ay satılan bot dağılımı
  const [monthlyUsageCountDist, setMonthlyUsageCountDist] = useState([]);  // bu ay bot bazlı mesaj kullanımı ort.
  const [monthlyUsageMinutesDist, setMonthlyUsageMinutesDist] = useState([]); // bu ay bot bazlı dakika kullanımı ort.

  const [yearlyBotData, setYearlyBotData] = useState([]);                  // bu yıl satılan bot dağılımı

  const [freeUserBotData, setFreeUserBotData] = useState([]);              // free kullanıcılar hangi botta daha çok
  // freeUserTokenUsageData gibi ayrı bir dağılım backend’den dönmüyorsa onu kaldırıyoruz

  useEffect(() => {
    const fetchDashboardSummary = async () => {
      try {
        const res = await axios.get(
          "/api/dashboard/summary",
          { withCredentials: true }
        );

        const { stats, distributions } = res.data;
        setStats(stats || {});

        // bunlar backend tarafındaki distributions ile hizalı olmalı
        setMonthlyBotData(distributions.monthlyPackages || []);
        setMonthlyUsageCountDist(distributions.monthlyUsageCountDist || []);
        setMonthlyUsageMinutesDist(distributions.monthlyUsageMinutesDist || []);
        setYearlyBotData(distributions.yearlyPackages || []);
        setFreeUserBotData(distributions.freePackages || []);
      } catch (err) {
        console.error("Dashboard verisi alınamadı:", err);
      }
    };

    fetchDashboardSummary();
  }, []);

  // renk paleti
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A020F0", "#EC407A", "#7E57C2"];

  // 🔁 Ortalanmış kart componenti
  const StatCard = ({ color, icon, label, value, width = 320, height = 180 }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      whileHover={{ scale: 1.05 }}
    >
      <Card
        sx={{
          width,
          height,
          p: 2,
          mb: 3,
          borderRadius: "20px",
          boxShadow: "0 16px 32px rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <CardContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ fontSize: 50, color, mb: 1, lineHeight: 1 }}>
            {icon}
          </Box>

          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "text.secondary",
              mb: 0.5,
              textAlign: "center",
            }}
          >
            {label}
          </Typography>

          <Typography variant="h4" fontWeight="bold" sx={{ textAlign: "center" }}>
            {value ?? 0}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );

  // 🔁 Tek tip PieChart bloğu (başlık + chart)
  const PieBlock = ({ title, data }) => (
    <Box sx={{ width: "500px", height: "400px", mb: 3 }}>
      <Typography
        variant="h6"
        textAlign="center"
        sx={{ mb: 1, fontWeight: "bold" }}
      >
        {title}
      </Typography>

      <ResponsiveContainer width="100%" height={360}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={120}
            label
          >
            {data.map((entry, idx) => (
              <Cell
                key={`cell-${idx}`}
                fill={COLORS[idx % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1, padding: 3, maxWidth: "1400px", margin: "auto" }}>
      {/* 📊 GENEL İSTATİSTİKLER */}
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        sx={{ mt: 3, mb: 3 }}
      >
        📊 Genel İstatistikler
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
        <StatCard
          label="Toplam Kullanıcı"
          value={stats.totalUsers}
          icon={<People fontSize="inherit" />}
          color="#1565C0"
        />
        <StatCard
          label="Toplam Satış (TL)"
          value={stats.totalSales}
          icon={<ShoppingCart fontSize="inherit" />}
          color="#2E7D32"
        />
        <StatCard
          label="Son Hafta Yeni Kullanıcı"
          value={stats.weeklyNewUsers}
          icon={<Timeline fontSize="inherit" />}
          color="#FF8F00"
        />
        <StatCard
          label="Aktif Paket Sayısı"
          value={stats.totalPackages}
          icon={<BarChart fontSize="inherit" />}
          color="#D32F2F"
        />
        <StatCard
          label="Son Hafta Ödemeler (TL)"
          value={stats.weeklyPayments}
          icon={<PaymentIcon fontSize="inherit" />}
          color="#6A1B9A"
        />
      </Stack>

      {/* 🆓 ÜCRETSİZ KULLANICILAR */}
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        sx={{ mt: 6, mb: 3 }}
      >
        🆓 Ücretsiz Kullananlar
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
        <StatCard
          label="Ücretsiz Kullanıcı Sayısı"
          value={stats.freeUsers}
          icon={<People fontSize="inherit" />}
          color="#D32F2F"
        />
        <StatCard
          label="Ücretsiz Mesaj Kullanımı"
          value={stats.freeUserTotalCountUsage}
          icon={<Message fontSize="inherit" />}
          color="#FF8F00"
        />
        <StatCard
          label="Ücretsiz Dakika Kullanımı"
          value={stats.freeUserTotalMinutesUsage}
          icon={<Schedule fontSize="inherit" />}
          color="#FF8F00"
        />
      </Stack>

      {/* 📊 PIE CHARTS (FREE USERS) */}
      <Stack
        direction="row"
        spacing={3}
        flexWrap="wrap"
        justifyContent="center"
        sx={{ mt: 4 }}
      >
        <PieBlock
          title="Ücretsiz Kullanıcıların Tercih Ettiği Botlar"
          data={freeUserBotData}
        />
      </Stack>

      {/* 📅 Aylık Veriler */}
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        sx={{ mt: 8, mb: 3 }}
      >
        📅 Aylık Veriler
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
        <StatCard
          label="Bu Ay Satılan Paket"
          value={stats.monthlySoldPackages}
          icon={<BarChart fontSize="inherit" />}
          color="#1976D2"
        />
        <StatCard
          label="Aylık Mesaj Kullanımı"
          value={stats.monthlyTotalCountUsage}
          icon={<Message fontSize="inherit" />}
          color="#FF5722"
        />
        <StatCard
          label="Aylık Dakika Kullanımı"
          value={stats.monthlyTotalMinutesUsage}
          icon={<Schedule fontSize="inherit" />}
          color="#FF5722"
        />
      </Stack>

      {/* 📊 Pie Charts (Aylık Dağılım) */}
      <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
        <PieBlock
          title="Bu Ay Satılan Botlar (Adet)"
          data={monthlyBotData}
        />
        <PieBlock
          title="Bot Bazlı Mesaj Kullanımı Ort. (Bu Ay)"
          data={monthlyUsageCountDist}
        />
        <PieBlock
          title="Bot Bazlı Dakika Kullanımı Ort. (Bu Ay)"
          data={monthlyUsageMinutesDist}
        />
      </Stack>

      {/* 📅 Yıllık Veriler */}
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        sx={{ mt: 8, mb: 3 }}
      >
        📅 Yıllık Veriler
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
        <StatCard
          label="Bu Yıl Satılan Bot"
          value={stats.yearlySoldPackages}
          icon={<BarChart fontSize="inherit" />}
          color="#673AB7"
        />
        <StatCard
          label="Yıllık Mesaj Kullanımı"
          value={stats.yearlyTotalCountUsage}
          icon={<Message fontSize="inherit" />}
          color="#E64A19"
        />
        <StatCard
          label="Yıllık Dakika Kullanımı"
          value={stats.yearlyTotalMinutesUsage}
          icon={<Schedule fontSize="inherit" />}
          color="#E64A19"
        />
      </Stack>

      {/* 📊 Pie Charts (Yıllık Satış Dağılımı) */}
      <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
        <PieBlock
          title="Bu Yıl Satılan Paketler (Adet)"
          data={yearlyBotData}
        />
      </Stack>
    </Box>
  );
};

export default DashboardHome;
